import React from "react";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";

// Render the placeholder in the normal (dark) text colour instead of MUI's faded grey,
// so a pinned-label dropdown reads like a native Select showing its chosen value.
const useStyles = makeStyles({
  darkPlaceholder: {
    "&::placeholder": { color: "rgba(0, 0, 0, 0.87)", opacity: 1 },
  },
});

/**
 * Alphabetically-sorted, type-to-filter dropdown (a thin wrapper over MUI lab Autocomplete).
 *
 * Props:
 *  - label        field label
 *  - options      [{ value, label }]  (sorted A→Z by label internally)
 *  - value        the currently-selected value (id/string); "" / null = none
 *  - onChange     (newValue) => void   — newValue is the selected option's value, or "" when cleared
 *  - disabled     optional
 *  - placeholder  optional (e.g. "-- select facility --")
 *  - noOptionsText optional
 *  - shrinkLabel  optional — keep the label pinned above the box (so `placeholder` shows inside
 *                 even when empty), matching a native Select with an InputLabel above it.
 */
export default function SearchableSelect({
  label,
  options,
  value,
  onChange,
  disabled = false,
  placeholder,
  noOptionsText,
  shrinkLabel = false,
}) {
  const classes = useStyles();
  const sorted = (options || [])
    .slice()
    .sort((a, b) => String(a.label || "").localeCompare(String(b.label || "")));
  const selected = sorted.find((o) => String(o.value) === String(value)) || null;

  return (
    <Autocomplete
      fullWidth
      openOnFocus
      options={sorted}
      value={selected}
      disabled={disabled}
      noOptionsText={noOptionsText || "No matches"}
      getOptionLabel={(o) => (o && o.label != null ? String(o.label) : "")}
      getOptionSelected={(o, v) => String(o.value) === String(v.value)}
      onChange={(e, val) => onChange(val ? val.value : "")}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputLabelProps={
            shrinkLabel
              ? { ...params.InputLabelProps, shrink: true }
              : params.InputLabelProps
          }
          inputProps={
            shrinkLabel
              ? {
                  ...params.inputProps,
                  className: `${params.inputProps.className || ""} ${classes.darkPlaceholder}`,
                }
              : params.inputProps
          }
        />
      )}
    />
  );
}
